// ---------------------------------------------------------------------------
// Urban 59 — Location configuration (single source of truth)
// ---------------------------------------------------------------------------
// Every location on the site is described by one entry in LOCATIONS below.
// Adding a third location later is just a new config entry — no new code.
//
// Fields:
//   id            unique machine id (used in EmailJS params, Firestore, keys)
//   slug          URL path segment, e.g. "sidcup" -> /sidcup
//   name          display name, e.g. "Urban 59 Sidcup"
//   shortName     short label for cards/nav, e.g. "Sidcup"
//   status        'open' | 'coming-soon'
//   enquiryType   'closer' | 'register-interest' | 'booking'
//                   closer           -> embed the Closer/LeadConnector iframe
//                   register-interest -> EmailJS-backed interest form (coming soon)
//                   booking          -> future booking embed when a site opens
//   closerFormId  LeadConnector form id (only used when enquiryType === 'closer')
//   layout        'rich' | 'generic'
//                   rich    -> full existing Sidcup layout (testimonials, video,
//                              live Firestore schedule, stats, etc.)
//                   generic -> reusable section renderer driven purely by config
//   address       { streetAddress, addressLocality, addressRegion, postalCode, addressCountry }
//   geo           { latitude, longitude } (optional; used for JSON-LD + geo meta)
//   mapUrl        Google Maps link (optional)
//   contactEmail  per-location contact email
//   phone         per-location phone (optional; falls back to brand phone)
//   hero          { title, description, backgroundVideo, poster, image } (optional parts)
//   tagline       short strapline
//   classTypes    [{ title, description }] (optional)
//   pricing       { from, to, currency, note } (optional)
//   timetable     { source: 'firestore' | 'static', configDoc, schedule } (optional)
//   facilities    [string] (optional)
//   openingHours  [{ days: [...], opens, closes }] for JSON-LD (optional)
//   seo           { title, description, keywords }
// ---------------------------------------------------------------------------

export const BRAND = {
	name: "Urban 59",
	url: "https://urban59.co.uk",
	email: "hello@urban59.co.uk",
	phone: "+447575391059",
	phoneDisplay: "07575 391059",
};

export const LOCATIONS = [
	// -------------------------------------------------------------------------
	// SIDCUP — open. Content mirrors what is live today.
	// -------------------------------------------------------------------------
	{
		id: "sidcup",
		slug: "sidcup",
		name: "Urban 59 Sidcup",
		shortName: "Sidcup",
		status: "open",
		enquiryType: "closer",
		closerFormId: "d13dUfgth2CbmYYiah68",
		layout: "rich",
		address: {
			streetAddress: "Lewis Road",
			addressLocality: "Sidcup",
			addressRegion: "Greater London",
			postalCode: "DA14 4NB",
			addressCountry: "GB",
		},
		geo: { latitude: 51.4308457, longitude: 0.1168816 },
		mapUrl: "https://g.page/r/CaptRLiGYbtCEBM",
		// Text query used for the keyless Google Maps embed in the footer.
		mapQuery: "Urban 59, 59 Lewis Road, Sidcup, DA14 4NB",
		contactEmail: "hello@urban59.co.uk",
		phone: "+447575391059",
		phoneDisplay: "07575 391059",
		hero: {
			title: "Build Strength.\nFeel Confident.\nLive Better.",
			description:
				"Experience small group personal training in Sidcup designed to help you get stronger, move better, and feel more energised every day. Train with purpose, look after your body, and create habits that truly change your lifestyle.",
			backgroundVideo: "landscape promo websiteLOW.mp4",
			poster: "WomanMattBW.jpg",
			image: "Sophie01.png",
		},
		tagline: "Small group personal training in Sidcup",
		pricing: {
			from: 80,
			to: 140,
			currency: "GBP",
			note: "Membership from £80 to £140 per month, depending on weekly sessions.",
		},
		// Sidcup pulls its live schedule from Firestore (config/schedule),
		// falling back to the default static grid baked into the Sidcup page.
		timetable: {
			source: "firestore",
			configDoc: "schedule",
		},
		facilities: [
			"Max 6 people per session",
			"Free parking nearby",
			"Beginner friendly",
			"Expert coaching",
		],
		openingHours: [
			{ days: ["Monday", "Thursday"], opens: "09:00", closes: "21:00" },
			{ days: ["Tuesday", "Wednesday"], opens: "09:00", closes: "20:00" },
			{ days: ["Friday"], opens: "11:00", closes: "19:00" },
			{ days: ["Saturday"], opens: "08:30", closes: "11:30" },
		],
		priceRange: "££",
		aggregateRating: { ratingValue: "5", reviewCount: "93" },
		// FAQ emitted as FAQPage JSON-LD on the Sidcup page (moved out of
		// index.html so it only appears where it's accurate).
		faq: [
			{
				q: "Where is Urban 59 Sidcup located?",
				a: "Urban 59 is on Lewis Road in Sidcup, Greater London (DA14).",
			},
			{
				q: "How much does Urban 59 cost?",
				a: "Membership ranges from £80 to £140 per month, depending on how many weekly sessions your plan includes.",
			},
			{
				q: "How big are the classes at Urban 59?",
				a: "Every session is capped at a maximum of 6 people, so you always get personal attention from a trainer.",
			},
			{
				q: "Is Urban 59 suitable for beginners?",
				a: "Yes. All levels are welcome, including complete beginners. Because the groups are small, your trainer guides you through every session.",
			},
			{
				q: "What happens in my first session at Urban 59?",
				a: "You book straight into a class. Because the groups are small, your trainer guides you through the workout and adjusts exercises to your level.",
			},
			{
				q: "Is there parking at Urban 59?",
				a: "Yes, there is free parking available.",
			},
		],
		seo: {
			title: "Urban 59 - Personal Training & Group Fitness Classes in Sidcup",
			description:
				"Transform your fitness at Urban 59 Sidcup. Expert personal training, small group sessions (max 6 people), and personalized coaching. Build strength, confidence, and energy. Join our welcoming fitness community today.",
			keywords:
				"personal training Sidcup, gym Sidcup, fitness classes, group training, strength training, fitness community, personal trainer, small group fitness, small group training",
		},
	},

	// -------------------------------------------------------------------------
	// EBBSFLEET — full Sidcup-style page (rich layout). Classes are capped at 16
	// people. Schedule is a fixed static timetable for now (admin-editable in
	// future, like Sidcup). Enquiries use the EmailJS "Get Started" form tagged
	// to Ebbsfleet so leads are attributed correctly.
	// TODO(ebbsfleet): replace address/postcode/geo/mapUrl/openingHours when known.
	// -------------------------------------------------------------------------
	{
		id: "ebbsfleet",
		slug: "ebbsfleet",
		name: "Urban 59 Ebbsfleet",
		shortName: "Ebbsfleet",
		status: "coming-soon",
		// EmailJS-backed "Get Started" enquiry form (location-tagged). Switch to
		// 'closer' + closerFormId once a dedicated Ebbsfleet Closer form exists.
		enquiryType: "enquiry",
		closerFormId: null,
		layout: "rich",
		address: {
			streetAddress: "The Blue Bean Coffee Co., 79 Cherry Orchard",
			addressLocality: "Ebbsfleet Valley, Swanscombe",
			addressRegion: "Kent",
			postalCode: "DA10 1AD",
			addressCountry: "GB",
		},
		// TODO(ebbsfleet): real coordinates. Null hides geo meta/JSON-LD geo block.
		geo: null,
		mapUrl:
			"https://www.google.com/maps/search/?api=1&query=The+Blue+Bean+Coffee+Co.,+79+Cherry+Orchard,+Ebbsfleet+Valley,+Swanscombe,+DA10+1AD",
		// Text query used for the keyless Google Maps embed in the footer.
		mapQuery:
			"The Blue Bean Coffee Co., 79 Cherry Orchard, Ebbsfleet Valley, Swanscombe, DA10 1AD",
		contactEmail: "hello@urban59.co.uk",
		phone: "+447575391059",
		phoneDisplay: "07575 391059",
		hero: {
			title: "Build Strength.\nFeel Confident.\nLive Better.",
			description:
				"Experience coach led strength training in Ebbsfleet designed to help you get stronger, move better, and feel more energised every day. Train with purpose, look after your body, and create habits that truly change your lifestyle.",
			backgroundVideo: "landscape promo websiteLOW.mp4",
			poster: "WomanMattBW.jpg",
			image: "Sophie01.png",
			// Primary hero CTA (also shown in the navbar in place of LOGIN).
			// The button opens an OfferModal presenting two purchase options
			// (intro offer for new members, and the 8-session class pack), each
			// linking out to its TeamUp page in a new tab. Hidden entirely if
			// `offer` is omitted (e.g. Sidcup).
			offer: {
				label: "Get Started",
				title: "Choose Your Option",
				intro:
					"Ready to train with us in Ebbsfleet? Pick the option that suits you best.",
				options: [
					{
						// icon = a lucide-react icon name (see OfferModal icon map).
						icon: "Sparkles",
						label: "Intro Offer",
						note: "New members only",
						price: "£2",
						priceSub: "2 sessions",
						description:
							"The perfect way to try us out and get started on the right foot.",
						cta: "Choose Intro Offer",
						highlight: true,
						badge: "New client offer",
						url: "https://goteamup.com/p/5559427-sophie-victoria-at-urban-/memberships/290583/",
					},
					{
						icon: "Dumbbell",
						label: "Class Pack",
						note: "8 sessions",
						price: "£127",
						priceSub: "8 sessions",
						description:
							"Train on your terms with a flexible pack of eight sessions.",
						cta: "Choose Class Pack",
						highlight: false,
						url: "https://goteamup.com/p/5559427-sophie-victoria-at-urban-/memberships/290689/",
					},
				],
			},
		},
		tagline: "Small group personal training in Ebbsfleet",
		// Enquiry form fields for this location (class-pack model). Ebbsfleet drops
		// the membership-oriented questions, keeping just contact details. Omit
		// this field entirely (like Sidcup) to show the full default form.
		enquiryFields: ["firstName", "lastName", "email", "phone"],
		// Ebbsfleet members don't have portal access yet, so hide the navbar
		// LOGIN button. With login hidden, the navbar shows the hero CTA
		// ("Buy a Class Pack") instead (when a real URL is set). Defaults to
		// showing login when this flag is omitted (e.g. Sidcup).
		showLogin: false,
		// About-section copy (Ebbsfleet wording, 16 per session).
		aboutDescription:
			"Train alongside people just like you, looking to feel better, move better, and build real confidence. Our small group personal training in Ebbsfleet is limited to 16 people per session, giving you the attention of personal training with the energy of a group. No overcrowded classes. No getting lost in the room. Just focused coaching, a supportive environment, and a space where you can truly look after your body.",
		// Stats grid hidden for now (we'll add Ebbsfleet numbers once the site is
		// more established). The "16 people per session" detail is still covered
		// in the about copy above. Add entries here to show the grid again.
		stats: [],
		// No Ebbsfleet-specific testimonials yet, so it shows the shared Urban 59
		// (Sidcup) testimonials for now. Add Ebbsfleet member stories here later
		// to override with its own.
		testimonials: [],
		// Fixed static schedule with a Sunday column (Sidcup is Mon–Sat).
		timetable: {
			source: "static",
			days: ["Tues", "Wed", "Sun"],
			schedule: {
				Tues: [
					{ time: "18:30", note: "" },
					{ time: "19:15", note: "" },
				],
				Wed: [
					{ time: "05:45", note: "" },
					{ time: "06:30", note: "" },
				],
				Sun: [
					{ time: "06:45", note: "" },
					{ time: "07:30", note: "" },
				],
			},
		},
		facilities: [
			// TODO(ebbsfleet): confirm facilities for the Ebbsfleet site
		],
		openingHours: [
			// TODO(ebbsfleet): confirm opening hours once known
		],
		seo: {
			title: "Urban 59 Ebbsfleet - Small Group Personal Training",
			description:
				"Small group personal training in Ebbsfleet. Coach-led sessions, 16 people per class, and personalised coaching. Build strength, confidence, and energy. Get started today.",
			keywords:
				"personal training Ebbsfleet, gym Ebbsfleet, fitness classes Ebbsfleet, small group training Kent, strength training, fitness community, Urban 59 Ebbsfleet",
		},
	},
];

// --- Helpers ---------------------------------------------------------------

/** Return all locations. */
export const getLocations = () => LOCATIONS;

/**
 * Build a keyless Google Maps embed URL for a location, suitable for an
 * <iframe src>. Uses the location's mapQuery, else its full address string.
 * No API key required (Spark/free-tier friendly). Returns null if there's no
 * usable address (e.g. unfilled TODO placeholder).
 */
export const getMapEmbedUrl = (location) => {
	if (!location) return null;
	let query = location.mapQuery;
	if (!query) {
		const a = location.address || {};
		if (!a.streetAddress || String(a.streetAddress).startsWith("TODO")) {
			return null;
		}
		query = [a.streetAddress, a.addressLocality, a.addressRegion, a.postalCode]
			.filter(Boolean)
			.join(", ");
	}
	return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
};

/** Find a location config by its slug (URL segment). */
export const getLocationBySlug = (slug) =>
	LOCATIONS.find((loc) => loc.slug === slug) || null;

/** Find a location config by its id. */
export const getLocationById = (id) =>
	LOCATIONS.find((loc) => loc.id === id) || null;

/** Convenience booleans. */
export const isOpen = (loc) => loc?.status === "open";
export const isComingSoon = (loc) => loc?.status === "coming-soon";
